import { useState } from 'react';
import type { FormEvent } from 'react';
import { track } from '@/lib/analytics';
import { createCheckoutSession, extractProfileIdentifier } from '@/lib/checkout';
import { useLang, useT } from '@/lib/useLang';

type Status = 'idle' | 'invalid' | 'loading' | 'failed';

interface UrlFormProps {
  /** `compact` = la variante du bloc CTA en bas de page. */
  variant?: 'hero' | 'compact';
  id?: string;
}

/**
 * L'URL LinkedIn saisie ici est la seule chose qu'on demande au visiteur.
 *
 * Elle part vers l'API qui ouvre la session Stripe ; l'email est collecté par
 * Stripe et pas par ce formulaire, donc il n'y a rien d'autre à saisir avant le
 * paiement. La maquette Claude Design affichait ici un faux message de
 * confirmation (« on revient vers vous ») — il n'y a plus de « on revient vers
 * vous » : on redirige vers le paiement.
 */
export function UrlForm({ variant = 'hero', id }: UrlFormProps) {
  const t = useT();
  const lang = useLang();
  const [url, setUrl] = useState('');
  const [status, setStatus] = useState<Status>('idle');

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === 'loading') return;

    const form = id ?? variant;
    track('checkout_form_submitted', { form, lang });

    const identifier = extractProfileIdentifier(url);
    if (!identifier) {
      track('checkout_url_invalid', { form, lang });
      setStatus('invalid');
      return;
    }

    setStatus('loading');
    try {
      const checkoutUrl = await createCheckoutSession({ linkedInUrl: url.trim(), lang });
      // Stripe héberge la page de paiement : on quitte le site, on ne revient
      // pas dans ce composant. Pas de `setStatus` après coup.
      track('checkout_redirected', { form, lang });
      window.location.assign(checkoutUrl);
    } catch {
      track('checkout_failed', { form, lang });
      setStatus('failed');
    }
  };

  const message =
    status === 'invalid' ? t.urlInvalid : status === 'failed' ? t.checkoutFailed : status === 'loading' ? t.redirecting : '';

  return (
    <div>
      <form className={`nz-form${variant === 'compact' ? ' nz-form--compact' : ''}`} onSubmit={onSubmit} noValidate>
        <input
          id={id}
          className="nz-form__input"
          type="text"
          inputMode="url"
          autoComplete="url"
          spellCheck={false}
          placeholder="linkedin.com/in/your-profile"
          aria-label="LinkedIn profile URL"
          aria-invalid={status === 'invalid'}
          value={url}
          onChange={(event) => {
            setUrl(event.target.value);
            if (status !== 'loading') setStatus('idle');
          }}
        />
        <button className="nz-form__submit" type="submit" disabled={status === 'loading'}>
          {t.formSubmit}
          <span aria-hidden="true">→</span>
        </button>
      </form>

      {message ? (
        <div className={`nz-notice${status === 'invalid' || status === 'failed' ? ' nz-notice--error' : ''}`} role="status">
          {message}
        </div>
      ) : null}
    </div>
  );
}
